import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type TitleLineCount = 1 | 2

interface ProductGridTitleSyncContextValue {
  titleLines: TitleLineCount
  reportTitleLines: (productId: string, lines: TitleLineCount) => void
}

const ProductGridTitleSyncContext =
  createContext<ProductGridTitleSyncContextValue | null>(null)

export function ProductGridTitleProvider({
  productIds,
  children,
}: {
  productIds: string[]
  children: ReactNode
}) {
  const [titleLines, setTitleLines] = useState<TitleLineCount>(1)
  const countsRef = useRef<Map<string, TitleLineCount>>(new Map())
  const idsKey = productIds.join('\0')

  useEffect(() => {
    countsRef.current.clear()
    setTitleLines(1)
  }, [idsKey])

  const reportTitleLines = useCallback(
    (productId: string, lines: TitleLineCount) => {
      if (countsRef.current.get(productId) === lines) return

      countsRef.current.set(productId, lines)
      let max: TitleLineCount = 1
      for (const count of countsRef.current.values()) {
        if (count === 2) {
          max = 2
          break
        }
      }
      setTitleLines((prev) => (prev === max ? prev : max))
    },
    []
  )

  const value = useMemo(
    () => ({ titleLines, reportTitleLines }),
    [titleLines, reportTitleLines]
  )

  return (
    <ProductGridTitleSyncContext.Provider value={value}>
      {children}
    </ProductGridTitleSyncContext.Provider>
  )
}

export function useProductGridTitleSync() {
  return useContext(ProductGridTitleSyncContext)
}
